/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Shimmer from 'sushiweb/Shimmer';
import toast from 'react-hot-toast';
import './User.scss';
import { Modal, CopyText, Confirm } from '../../../UiKit';
import { black } from 'sushiweb/tokens/color';
import { UserContext } from '../../popups';
import { userModel } from '../../../api';
import { getMilliseconds } from '../../commons/CommonMethods';
import { sendEventToJumbo } from '../../../helpers/analytics';
import {
  sendUserApiEvents,
  sendUserUnmaskEvent,
  sendUserModalEvent,
  USER_EVENTS,
} from '../../../helpers/analytics/user.analytics';
import { PaymentDetailV1 } from '../Order';
import { ChevronRight, Cross } from 'sushiweb/Icons';
import { OrderTag } from '../Order/OrderBasicInfo/OrderTags';
import {
  TriangleAlert,
  User as UserIcon,
  UserRound,
  Users,
} from 'lucide-react';
import { AlternateNumberV1 } from './atoms/AlternateNumberV1';
import { UserNumberV1 } from './atoms/UserNumberV1';
import { UserEmailV1 } from './atoms/UserEmailV1';
import { ToolTip } from '../../ComponentsV1/ToolTip/Tooltip';
import { InfoStrip } from '../Order/OrderBasicInfo/OrderTagDetailsModal';
import { EmptyState } from '../../commons';
import { InvalidSearchImg } from '../../../images/images';

const calledTo = 'Customer';

const InfoStripV1 = ({ title, value, isId, idUrl }) => {
  return (
    <div className='tw-w-full tw-flex tw-justify-start tw-items-center'>
      <span className='tw-text-lg tw-w-1/2 tw-p-3 tw-font-medium tw-text-zinc-700 tw-leading-none'>
        {title || '-'}
      </span>
      <span
        onClick={() => {
          if (idUrl) {
            window.open(idUrl, '_blank', 'noopener,noreferrer');
          }
        }}
        className={`tw-font-medium tw-border tw-border-solid !tw-border-y-0  !tw-border-r-0 tw-border-grey-200 tw-w-1/2 tw-flex tw-items-center tw-justify-start tw-capitalize tw-leading-none  ${
          idUrl ? 'tw-underline tw-cursor-pointer tw-underline-offset-2' : ''
        }`}
      >
        <span
          className={`${
            isId
              ? 'tw-mx-3 tw-mt-2 tw-p-2 !tw-w-fit tw-whitespace-nowrap tw-leading-normal tw-bg-grey-200/70 tw-rounded-lg tw-text-zinc-700 tw-text-[1.4rem]'
              : 'tw-text-sm tw-text-zinc-700 tw-p-3'
          }`}
          data-dd-privacy='mask'
        >
          {isId && value ? <CopyText title={value} /> : value || '-'}
        </span>
      </span>
    </div>
  );
};

const SuspectIdentifierDetailsComponent = ({ identityData }) => {
  return (
    <React.Fragment>
      <div className='tw-grid tw-grid-cols-1 tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg'>
        <div className='tw-flex tw-flex-col'>
          {Object.entries(identityData).map(([key, value]) => {
            return (
              <InfoStripV1
                key={key}
                title={key}
                value={value}
                isId={
                  /id\b/i.test(key) || key.toLocaleLowerCase() === 'marked by'
                }
              />
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
};

export const User = ({
  hasAnyOrders = true,
  orderId = null,
  userId = null,
  isWFH = false,
  isNewUser = false,
  getOrderBasicInfo,
  orderBasicInfo,
  showCxContext = true,
}) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, userId]);

  const getUserData = useCallback(() => {
    let queryStrings = {};
    queryStrings['order_id'] = orderId;
    if (isNewUser) {
      queryStrings['user_id'] = userId;
    }
    userModel
      .getUserData(queryStrings)
      .then((response) => {
        if (!response || response?.isError) return null;
        sendUserApiEvents(
          USER_EVENTS.GET_USER_DETAILS_SUCCESS,
          orderId,
          userId,
        );
        setUser({ ...response });
      })
      .catch(() => {
        sendUserApiEvents(
          USER_EVENTS.GET_USER_DETAILS_FAILURE,
          orderId,
          userId,
        );
      });
  }, [orderId, userId, isNewUser]);

  return (
    <div className='userContainer tw-h-full'>
      {!user ? (
        <Shimmer height='100%' width='100%' />
      ) : (
        <UserComp
          hasAnyOrders={hasAnyOrders}
          orderId={orderId}
          isWFH={isWFH}
          user={user}
          userId={userId}
          isNewUser={isNewUser}
          getOrderBasicInfo={getOrderBasicInfo}
          orderBasicInfo={orderBasicInfo}
          showCxContext={showCxContext}
        />
      )}
    </div>
  );
};

const UserComp = ({
  hasAnyOrders,
  user,
  orderId,
  isWFH,
  userId,
  isNewUser = false,
  getOrderBasicInfo,
  orderBasicInfo,
  showCxContext,
}) => {
  const [userNumberInfo, setUserNumberInfo] = useState(null);
  const [userEmailInfo, setUserEmailInfo] = useState(null);
  const [ofseInfo, setOfseInfo] = useState(null);
  const [altContact, setAltContact] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [isOfseClicked, setOfseClicked] = useState(false);
  const [isAltContactClicked, setAltContactClicked] = useState(false);
  const [customerDeviceId, setCustomerDeviceId] = useState(null);

  const maskUserInfo = useCallback(() => {
    if (!userNumberInfo || (userNumberInfo && !userNumberInfo.valid_till))
      return;
    const { valid_till } = userNumberInfo;
    const getValidMilliseconds = getMilliseconds(valid_till);
    const timerId = setTimeout(() => {
      setUserNumberInfo(null);
      toast.error(userNumberInfo.message ?? 'Masked number validity expired!');
      clearTimeout(timerId);
    }, getValidMilliseconds * 1000);
  }, [userNumberInfo]);

  const makeCallForUnmaskUser = useCallback(
    (type, isForced) => {
      let queryStrings = {
        order_id: orderId,
      };
      if (isNewUser) {
        queryStrings['user_id'] = userId;
      }
      if (isForced) {
        queryStrings['force_allocate'] = isForced;
      }

      return userModel
        .unmaskUserInfo(queryStrings, type)
        .then((response) => {
          if (
            !response ||
            (response && response.isError === true) ||
            (response && response.status === 'failure')
          ) {
            toast.error(
              response.message ||
                'Something went wrong. please try again later!',
            );
          } else {
            return response;
          }
        })
        .catch((err) => {
          toast.error(
            err.message || 'Something went wrong. please try again later!',
          );
        });
    },
    [orderId, userId, isNewUser],
  );

  const unmaskUserNumber = useCallback(
    (isForced) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('number', isForced).then((res) => {
        setUserNumberInfo(res);
        maskUserInfo();
      });
    },
    [orderId, userId, makeCallForUnmaskUser, maskUserInfo],
  );

  const unmaskUserOfseNumber = useCallback(
    (isForced = false) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_OFSE_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('ofse_number', isForced).then((res) => {
        setOfseInfo(res);
      });
    },
    [orderId, userId, makeCallForUnmaskUser],
  );

  const unmaskUserAltNumber = useCallback(
    (isForced = false) => {
      sendUserUnmaskEvent(
        USER_EVENTS.UNMASK_USER_ALTERNATE_NUMBER_EVENT,
        orderId,
        userId,
      );
      makeCallForUnmaskUser('alternative_number', isForced).then((res) => {
        setAltContact(res);
      });
    },
    [orderId, userId, makeCallForUnmaskUser],
  );

  const unmaskUserEmail = useCallback(() => {
    sendUserUnmaskEvent(USER_EVENTS.UNMASK_USER_EMAIL_EVENT, orderId, userId);
    makeCallForUnmaskUser('email').then((res) => {
      setUserEmailInfo(res);
    });
  }, [orderId, userId, makeCallForUnmaskUser]);

  const reGeneratedMaskNumber = useCallback(
    (details, isOfse = false, isAlt = false) => {
      let eventName = isAlt
        ? USER_EVENTS.REGENERATE_USER_ALTERNATE_NUMBER_EVENT
        : isOfse
        ? USER_EVENTS.REGENERATE_USER_OFSE_NUMBER_EVENT
        : USER_EVENTS.REGENERATE_USER_NUMBER_EVENT;
      sendUserUnmaskEvent(eventName, orderId, userId);
      setConfirmationDetails(details);
      setOfseClicked(isOfse);
      setAltContactClicked(isAlt);
    },
    [orderId, userId],
  );

  const handleConfirmClick = useCallback(
    (action) => {
      let isForced = true;
      setConfirmationDetails(null);

      if (action === 'cancel') {
        return;
      }

      if (isOfseClicked) {
        unmaskUserOfseNumber(isForced);
      } else if (isAltContactClicked) {
        unmaskUserAltNumber(isForced);
      } else {
        unmaskUserNumber(isForced);
      }
    },
    [
      isOfseClicked,
      isAltContactClicked,
      unmaskUserOfseNumber,
      unmaskUserAltNumber,
      unmaskUserNumber,
    ],
  );

  const openUserContextModal = useCallback(() => {
    sendUserModalEvent(USER_EVENTS.USER_CONTEXT_MODAL_OPEN, orderId, userId);
    let details = {
      title: (
        <div className='tw-flex tw-items-center tw-gap-2 tw-text-[1.8rem] tw-font-medium'>
          <UserIcon size={20} strokeWidth={2.5} />
          Customer Overview
        </div>
      ),
      size: 'large',
      maxHeight: '80vh',
      label: user?.user_karma?.karma,
      bgColor: user?.user_karma?.karma_btn_color,
      color: user?.user_karma?.karma_txt_color,
      isMaxContent: true,
      component: (
        <div className='user_context'>
          <UserContext
            orderId={orderId}
            userId={user.id || userId}
            currentKarma={{
              label: user?.user_karma?.karma,
              bgColor: user?.user_karma?.karma_btn_color,
              textColor: user?.user_karma?.karma_txt_color,
            }}
            setCustomerDeviceId={setCustomerDeviceId}
          />
        </div>
      ),
    };
    setPopupData(details);
  }, [user, orderId, userId]);

  const showSuspectDetails = (suspect_details_meta) => {
    let component = <div />;

    if (
      !suspect_details_meta ||
      !Array.isArray(suspect_details_meta.data) ||
      suspect_details_meta.data.length === 0
    ) {
      component = (
        <EmptyState
          img={InvalidSearchImg}
          heading={'No suspect data available.'}
        />
      );
    } else {
      component = (
        <div className='tw-flex tw-flex-col tw-gap-7 tw-mt-8'>
          {suspect_details_meta.data?.map((item) => (
            <SuspectIdentifierDetailsComponent
              key={Object.values(item)?.[0]}
              identityData={item}
            />
          ))}
        </div>
      );
    }

    let details = {
      title: suspect_details_meta.title,
      size: 'large',
      maxHeight: '80vh',
      isMaxContent: true,
      component: component,
    };
    setPopupData(details);
  };

  const parentMessageListner = useCallback(
    (event) => {
      const { data: { source = '', action = '', additionalData = {} } = {} } =
        event;
      if (source === 'oneSupport' && action === 'openCustomerContext' && user) {
        if (
          Number(additionalData.transactionId || 0) === Number(orderId || 0) &&
          Number(additionalData.userId) === Number(user.id)
        ) {
          openUserContextModal();
          sendEventToJumbo('smartplug_options_event_click', {
            action: action,
            order_id: String(orderId || 0),
            user_id: String(user.id),
          });
        }
      }
    },
    [user, orderId, openUserContextModal],
  );

  useEffect(() => {
    window.addEventListener('message', parentMessageListner);
    return () => {
      window.removeEventListener('message', parentMessageListner);
    };
  }, [parentMessageListner]);

  const handleModalClose = useCallback(() => {
    setPopupData(null);
    sendUserModalEvent(USER_EVENTS.USER_MODAL_CLOSE, orderId, userId);
  }, [orderId, userId]);

  return (
    <div
      className={`tw-flex-col tw-border-solid tw-border-[1px] tw-rounded-[8px] tw-border-grey-200 tw-h-full ${
        hasAnyOrders ? '' : 'tw-w-[35rem]'
      }`}
    >
      {/* Upper section */}
      <div className='tw-border-solid tw-h-[65px] tw-border-[1px] tw-border-grey-200 tw-border-x-0 tw-border-t-0 tw-p-2 tw-flex tw-items-center tw-gap-2'>
        {/* Customer icon */}
        <div className='tw-h-full tw-aspect-square tw-bg-teal-100 tw-flex-col tw-rounded-[6px] tw-justify-center tw-items-center tw-border-solid tw-border-teal-200/50 tw-border-[1px] suspect-parent tw-transition-all tw-duration-300 tw-ease-in-out'>
          <div className='tw-h-full tw-flex tw-justify-center tw-items-center'>
            {user?.tags?.user_suspect_details?.is_suspect ? (
              <ToolTip
                content={user.tags.user_suspect_details.text || 'Suspect'}
              >
                <div
                  className='tw-cursor-pointer tw-p-3 hover:tw-animate-none tw-transition-all tw-duration-200 tw-ease-in-out'
                  onMouseEnter={(e) => {
                    e.currentTarget.style.animation =
                      'vibrate 0.6s linear infinite';
                    document
                      .querySelector('.suspect-parent')
                      ?.classList.add(
                        '!tw-bg-orange-200/50',
                        '!tw-border-orange-200',
                      );
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.animation = 'none';
                    document
                      .querySelector('.suspect-parent')
                      ?.classList.remove(
                        '!tw-bg-orange-200/50',
                        '!tw-border-orange-200',
                      );
                  }}
                  onClick={() =>
                    showSuspectDetails(user.tags.user_suspect_details.meta)
                  }
                >
                  <TriangleAlert
                    size={21}
                    className='tw-text-orange-500'
                    fill='#fff1e6'
                  />
                </div>
              </ToolTip>
            ) : user.digital_ofse_receiver_details?.count ? (
              <ToolTip
                content={`No of receiver(s): ${user.digital_ofse_receiver_details.count}`}
                placement='right'
              >
                <div className='tw-cursor-pointer'>
                  <Users size={22} color='#000000' />
                </div>
              </ToolTip>
            ) : (
              <UserRound size={22} />
            )}
          </div>
          {user.ofse_details?.label && (
            <div
              style={{
                backgroundColor: user.ofse_details.label_btn_color || '#0a757c',
                color: 'white',
              }}
              className={`tw-rounded-b-[6px] ${
                user.ofse_details?.label === 'Sender'
                  ? 'tw-text-[1.4rem] '
                  : 'tw-text-[1.3rem] tw-px-1'
              } tw-flex tw-items-center tw-justify-center tw-w-full tw-gap-1 tw-font-medium`}
            >
              {user.ofse_details?.label}
            </div>
          )}
        </div>

        <div className='tw-flex tw-flex-col tw-h-full tw-w-full tw-justify-between'>
          <div className='tw-flex tw-justify-between tw-w-full tw-items-center tw-gap-1'>
            <div className='tw-text-zinc-500 tw-font-medium  tw-flex tw-gap-1'>
              <span className='tw-whitespace-nowrap'>Customer #</span>
              <CopyText title={user.id} />
            </div>
            <UserTags user={user} />
          </div>

          <div className='tw-flex tw-items-end tw-justify-between'>
            <ToolTip
              content={user.name || ''}
              placement='bottom'
              maskContent={true}
            >
              <span
                className={`tw-text-[16px] tw-h-full tw-text-zinc-600 tw-font-semibold tw-leading-none tw-flex tw-items-end tw-pb-1 tw-duration-200 tw-cursor-pointer tw-transition-all tw-ease-in-out hover:tw-text-base-green hover:tw-opacity-90 tw-whitespace-nowrap tw-overflow-hidden
                    ${
                      user.user_blocking_details?.is_blocked &&
                      Object.keys(user.delete_status || {}).length > 0
                        ? 'tw-max-w-[15rem]'
                        : user.user_blocking_details?.is_blocked ||
                          Object.keys(user.delete_status || {}).length > 0
                        ? 'tw-max-w-[18rem]'
                        : 'tw-max-w-[30rem]'
                    }
                    tw-text-center tw-text-ellipsis`}
                onClick={openUserContextModal}
                style={{
                  textDecoration: 'underline dashed',
                  textUnderlineOffset: '2px',
                }}
                data-dd-action-name='See Customer Overview'
                data-dd-privacy='mask'
              >
                {user.name}
              </span>
            </ToolTip>
            <div className='tw-flex tw-items-center tw-gap-2'>
              {/* User blocked tag */}
              {user?.user_blocking_details?.is_blocked && (
                <ToolTip
                  content={user.user_blocking_details.reason}
                  placement={'bottom'}
                  disabled={!user.user_blocking_details.reason}
                >
                  <div>
                    <OrderTag
                      bgColor={
                        user.user_blocking_details.blocked_btn_color || 'red'
                      }
                      textColor={'white'}
                      showInfoIcon={user.user_blocking_details.reason}
                      showHoverPointer={user.user_blocking_details.reason}
                    >
                      Blocked
                    </OrderTag>
                  </div>
                </ToolTip>
              )}

              {Object.keys(user.delete_status || {})?.length > 0 ? (
                <ToolTip
                  placement='bottom'
                  content={user.delete_status.hover_text}
                >
                  <div>
                    <OrderTag
                      bgColor={user.delete_status.label_btn_color}
                      textColor={user.delete_status.text_color}
                    >
                      {user.delete_status.label}
                    </OrderTag>
                  </div>
                </ToolTip>
              ) : null}

              {/* Bistro Fraud tag */}
              {user?.bistro_fraud_details?.text ? (
                <FraudTag user={user} setPopupData={setPopupData} />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Mid section */}
      <div
        className={`tw-border-solid tw-h-[95px] tw-border-[1px] tw-border-grey-200 tw-border-x-0 tw-border-t-0 ${
          showCxContext ? '' : '!tw-border-b-0'
        } tw-p-4 tw-flex-col tw-gap-4 tw-bg-grey-100/40`}
      >
        <div className='tw-flex tw-items-center tw-justify-between tw-h-7'>
          <UserNumberV1
            number={user.number}
            numberInfo={userNumberInfo}
            calledTo={calledTo}
            isWFH={isWFH}
            orderId={orderId}
            userId={userId}
            isNewUser={isNewUser}
            unmaskNumber={unmaskUserNumber}
            regenerateNumber={reGeneratedMaskNumber}
            cartId={orderBasicInfo?.order?.cart_id}
          />
          {user?.tags?.employee_details?.text ? (
            <div className='tw-text-[1.3rem] tw-border-solid tw-text-zinc-600 tw-font-medium tw-border-teal-200 tw-border-[1px] tw-rounded-md tw-bg-teal-100/50 tw-px-2 tw-py-[0.4rem] tw-whitespace-nowrap tw-leading-none tw-w-fit tw-ml-2'>
              {user.tags.employee_details.text}
            </div>
          ) : null}
        </div>

        {user.has_alternative_contact && (
          <AlternateNumberV1
            number={user.alternative_number}
            numberInfo={altContact}
            calledTo={calledTo}
            isWFH={isWFH}
            orderId={orderId}
            userId={userId}
            isNewUser={isNewUser}
            unmaskNumber={unmaskUserAltNumber}
            regenerateNumber={reGeneratedMaskNumber}
            alternateContactName={
              user?.alternative_contact_name || 'Order Contact Number'
            }
            cartId={orderBasicInfo?.order?.cart_id}
          />
        )}

        <UserEmailV1 emailInfo={userEmailInfo} onClick={unmaskUserEmail} />
      </div>

      {/* lower section */}
      {showCxContext && (
        <div className='tw-px-3 tw-py-4 tw-pr-5 tw-w-full tw-flex-1'>
          <PaymentDetailV1
            orderId={orderId}
            userId={userId}
            order={orderBasicInfo?.order}
            retry={getOrderBasicInfo}
            cartId={orderBasicInfo?.order?.cart_id}
          />
        </div>
      )}

      <Modal
        isVisible={popupData}
        type={popupData?.size}
        padding={0}
        containerStyles={{
          margin: '16px 0 20px 0',
          padding: '0 20px',
          borderTop: '1px solid #e8e8e8',
        }}
        paddingTop={'5px'}
        maxHeight={popupData?.maxHeight}
        titleComponent={
          <div className='tw-flex tw-justify-between tw-items-center tw-w-full'>
            <div className='tw-flex tw-items-start tw-flex-col tw-justify-center tw-gap-2 tw-flex-grow tw-shrink-0 tw-basis-0 tw-text-[18px]'>
              <div className='tw-self-stretch tw-text-[#1c1c1c] tw-text-[20px] tw-font-medium'>
                {popupData?.title}
              </div>
              {customerDeviceId && (
                <div className='tw-font-medium tw-text-grey-800'>
                  <span>Device ID:</span>
                  <CopyText title={customerDeviceId} />
                </div>
              )}
            </div>
            <div
              className='tw-mr-6 tw-cursor-pointer'
              onClick={handleModalClose}
            >
              <Cross size={20} color={black} />
            </div>
          </div>
        }
        showCloseIcon={false}
        handleClose={handleModalClose}
        component={popupData?.component}
      />

      {confirmationDetails ? (
        <Confirm
          show={true}
          content={confirmationDetails}
          close={() => setConfirmationDetails(null)}
          handleClick={handleConfirmClick}
        />
      ) : null}
    </div>
  );
};

const UserTags = ({ user }) => {
  const orgId = sessionStorage.getItem('org_id');
  const [showAllTags, setShowAllTags] = useState(false);

  // Define all possible tags with their render functions
  const allTags = useMemo(() => {
    if (!user) return [];
    const tags = [];

    // User Karma
    if (user?.user_karma?.karma) {
      tags.push({
        id: 'karma',
        component: (
          <ToolTip
            content={user.user_karma.karma_source || ''}
            placement='bottom'
          >
            <div>
              <OrderTag
                bgColor={user.user_karma.karma_btn_color}
                textColor={user.user_karma.karma_txt_color}
                showInfoIcon
                showHoverPointer
              >
                {user.user_karma.karma}
              </OrderTag>
            </div>
          </ToolTip>
        ),
      });
    }

    // Channel
    if (user.channel) {
      tags.push({
        id: 'channel',
        component: (
          <div
            key='channel'
            className={`karmaLabel tw-flex tw-items-center tw-h-10 tw-w-20 tw-rounded-md ${
              orgId == 1 ? 'tw-bg-base-yellow' : ''
            } tw-bg-contain tw-overflow-hidden`}
          >
            {user.channel.image ? (
              <img
                src={user.channel.image}
                alt='channel'
                className={`tw-w-full tw-h-full ${
                  orgId == 1 ? '' : 'tw-object-fill'
                }`}
              />
            ) : (
              <OrderTag
                bgColor={user.channel.label_btn_color}
                textColor={user.channel.text_color}
              >
                {user.channel.label}
              </OrderTag>
            )}
          </div>
        ),
      });
    }

    // Concierge Data
    if (user.concierge_data?.tag) {
      tags.push({
        id: 'concierge',
        component: (
          <ToolTip
            content={user.concierge_data.reason || ''}
            placement='bottom'
          >
            <div>
              <OrderTag bgColor={user.concierge_data.color}>
                {user.concierge_data.tag}
              </OrderTag>
            </div>
          </ToolTip>
        ),
      });
    }

    // FDP
    if (user.fdp?.should_show_fdp_info) {
      tags.push({
        id: 'fdp',
        component: (
          <OrderTag key='fdp' bgColor={user.fdp.color}>
            {user.fdp.title}
          </OrderTag>
        ),
      });
    }

    // COD Block Reason
    if (user.cod_block_reason?.length) {
      tags.push({
        id: 'cod_block',
        component: (
          <ToolTip content={user.cod_block_reason || ''} placement='bottom'>
            <div>
              <OrderTag bgColor='#FF6B35'>COD Blocked</OrderTag>
            </div>
          </ToolTip>
        ),
      });
    }

    return tags;
  }, [user, orgId]);

  // Split tags into visible and hidden
  const visibleTags = allTags.slice(0, 1);
  const hiddenTags = allTags.slice(1);
  const shouldShowChevron = allTags.length > 1;

  const handleMouseEnter = () => {
    clearTimeout(window.hideUserTagsTimeout);
    setShowAllTags(true);
  };

  const handleMouseLeave = () => {
    window.hideUserTagsTimeout = setTimeout(() => {
      setShowAllTags(false);
    }, 300);
  };

  // Don't render if no tags
  if (allTags.length === 0) return null;

  return (
    <div>
      <div className='tw-flex tw-justify-center tw-gap-2 tw-h-10'>
        {/* Always visible tags (first 2) */}
        {visibleTags.map((tag) => tag.component)}

        {/* Chevron button - only show if more than 2 tags */}
        {shouldShowChevron && (
          <div className='tw-relative tw-inline-block'>
            <div
              className='tw-cursor-pointer tw-w-6 tw-rounded-md tw-flex tw-items-center tw-justify-center hover:tw-bg-grey-200/50 tw-transition-all tw-duration-200 tw-ease-in-out tw-p-[2px]'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <ChevronRight
                size={22}
                aria-hidden
                focusable='false'
                color='#318616'
              />
            </div>

            {/* Popover with remaining tags */}
            <div
              className={`tw-absolute tw-left-full tw-top-0 tw-ml-2 tw-z-20 tw-rounded-lg tw-bg-grey-200 tw-shadow-md tw-flex tw-items-start tw-justify-center tw-p-2 tw-flex-col tw-gap-2 tw-transition-all tw-duration-200 tw-ease-out ${
                showAllTags
                  ? 'tw-opacity-100 tw-translate-x-0 tw-scale-100'
                  : 'tw-opacity-0 tw-translate-x-[-8px] tw-scale-95 tw-pointer-events-none'
              }`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <span className='tw-font-medium tw-text-[1.2rem] tw-leading-tight tw-whitespace-nowrap'>
                Other User Tags:
              </span>
              <div className='tw-flex tw-flex-wrap tw-justify-center tw-gap-2 tw-max-w-xs'>
                {hiddenTags.map((tag) => tag.component)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FraudTag = ({ user = {}, setPopupData }) => {
  const {
    bistro_fraud_details: {
      bg_color = 'red',
      color = 'white',
      meta = {},
      text = '',
    } = {},
  } = user || {};

  const handleClick = () => {
    if (!meta || !meta.data) return;
    setPopupData &&
      setPopupData({
        title: meta?.title || 'Fraud Details',
        size: 'medium',
        maxHeight: '80vh',
        isMaxContent: true,
        component: (
          <div className='tw-w-full tw-flex tw-flex-col tw-gap-2'>
            {Array.isArray(meta.data) &&
              meta.data.map((elements, index) => (
                <div
                  className='tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg tw-bg-white'
                  key={index}
                >
                  <div className='tw-flex tw-flex-col tw-gap-4 tw-p-4'>
                    {Array.isArray(elements) &&
                      elements.map((info) => {
                        if (!info) return null;
                        return (
                          <InfoStrip
                            key={info.key}
                            title={info.key}
                            value={`${info.value}`}
                            isId={false}
                          />
                        );
                      })}
                  </div>
                </div>
              ))}
          </div>
        ),
      });
  };

  return (
    <OrderTag
      key='bistro_fraud'
      bgColor={bg_color}
      textColor={color}
      showArrow={!!meta}
      showHoverPointer={!!meta}
      onClick={handleClick}
    >
      <span className='tw-capitalize'>{text}</span>
    </OrderTag>
  );
};

export default UserTags;
